"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Loader2 } from "lucide-react"
import { authAPI, legalAPI } from "@/lib/api"

interface Requirement {
  docType: 'terms' | 'privacy'
  version: string
  title: string
  contentHtml: string
}

interface LegalConsentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  email: string
  password: string
  requirements: Requirement[]
  onSuccess: () => void
  onCancel: () => void
}

export default function LegalConsentDialog({ open, onOpenChange, email, password, requirements, onSuccess, onCancel }: LegalConsentDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [ackPenalty, setAckPenalty] = useState(false)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activeTerms = requirements.find(r => r.docType === 'terms')
  const activePrivacy = requirements.find(r => r.docType === 'privacy')

  const canSubmit = (!!activeTerms ? (acceptTerms && ackPenalty) : true) && (!!activePrivacy ? acceptPrivacy : true)

  const docCount = (activeTerms ? 1 : 0) + (activePrivacy ? 1 : 0)
  const docHeightClass = docCount === 2 ? 'h-[36dvh]' : 'h-[58dvh]'

  async function handleSubmit() {
    try {
      setIsLoading(true)
      setError(null)
      const consents = activeTerms ? [{ docType: 'terms' as const, version: activeTerms.version, penaltyNoticeAck: true as const }] : []
      const res = await authAPI.submitConsent({
        email,
        password,
        consents,
        privacyAccepted: !!activePrivacy && acceptPrivacy
      })
      if (res && !res.requiresConsent) {
        onSuccess()
        onOpenChange(false)
      } else {
        setError('Zustimmung fehlgeschlagen. Bitte erneut versuchen.')
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Fehler bei der Zustimmung.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] !w-[100vw] sm:!w-auto !h-[100dvh] sm:!h-auto sm:max-h-[85vh] !p-3 sm:!p-6 !box-border overflow-hidden flex flex-col min-h-0 !top-0 !left-0 !translate-x-0 !translate-y-0 sm:!left-1/2 sm:!top-1/2 sm:!translate-x-[-50%] sm:!translate-y-[-50%] !rounded-none sm:!rounded-lg">
        <DialogHeader className="sticky top-0 z-10 bg-background pb-2">
          <DialogTitle>Rechtliche Zustimmung erforderlich</DialogTitle>
          <DialogDescription>
            Bitte lesen und akzeptieren Sie die folgenden Dokumente, um fortzufahren.
          </DialogDescription>
        </DialogHeader>

        {/* Mobile: Accordion layout */}
        <div className="sm:hidden flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-4">
          <Accordion type="single" collapsible defaultValue={activeTerms ? 'terms' : 'privacy'}>
            {activeTerms && (
              <AccordionItem value="terms">
                <AccordionTrigger className="text-left">AGB – {activeTerms.title} (Version {activeTerms.version})</AccordionTrigger>
                <AccordionContent>
                  <ScrollArea className="h-[50dvh] rounded-md border p-4 -mx-1">
                    <div
                      className="text-sm leading-6 max-w-none [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-0 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                      dangerouslySetInnerHTML={{ __html: activeTerms.contentHtml }}
                    />
                  </ScrollArea>
                  <p className="text-xs text-muted-foreground mt-2">
                    Für schuldhafte Verstöße gelten 25.000 € (IV.2 g) bzw. 100.000 € (IV.2 h) pro Verstoß; Näheres regelt Ziff. IV.4. [IV.2 g)] [IV.2 h)] [IV.4]
                  </p>
                </AccordionContent>
              </AccordionItem>
            )}
            {activePrivacy && (
              <AccordionItem value="privacy">
                <AccordionTrigger className="text-left">Datenschutzerklärung – {activePrivacy.title} (Version {activePrivacy.version})</AccordionTrigger>
                <AccordionContent>
                  <ScrollArea className="h-[50dvh] rounded-md border p-4 -mx-1">
                    <div
                      className="text-sm leading-6 max-w-none [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-0 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                      dangerouslySetInnerHTML={{ __html: activePrivacy.contentHtml }}
                    />
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {error && (
            <div className="text-sm text-destructive mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">{error}</div>
          )}
        </div>

        {/* Desktop/tablet: Dual panels */}
        <div className="hidden sm:block flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-6">
          {activeTerms && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">AGB – {activeTerms.title} (Version {activeTerms.version})</h3>
              <ScrollArea className={`${docHeightClass} sm:h-56 rounded-md border p-4 -mx-1`}>
                <div
                  className="text-sm leading-6 max-w-none [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-0 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                  dangerouslySetInnerHTML={{ __html: activeTerms.contentHtml }}
                />
              </ScrollArea>
              <p className="text-xs text-muted-foreground">
                Für schuldhafte Verstöße gelten 25.000 € (IV.2 g) bzw. 100.000 € (IV.2 h) pro Verstoß; Näheres regelt Ziff. IV.4. [IV.2 g)] [IV.2 h)] [IV.4]
              </p>
            </div>
          )}

          {activePrivacy && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Datenschutzerklärung – {activePrivacy.title} (Version {activePrivacy.version})</h3>
              <ScrollArea className={`${docHeightClass} sm:h-56 rounded-md border p-4 -mx-1`}>
                <div
                  className="text-sm leading-6 max-w-none [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-0 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                  dangerouslySetInnerHTML={{ __html: activePrivacy.contentHtml }}
                />
              </ScrollArea>
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">{error}</div>
          )}
        </div>

        {/* Bottom confirmation checkboxes */}
        <div className="space-y-2 pt-2 mt-2 border-t">
          {activeTerms && (
            <>
              <div className="flex items-center space-x-2">
                <Checkbox id="accept-terms" checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} />
                <label htmlFor="accept-terms" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Ich akzeptiere die AGB (Version {activeTerms.version}).
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="ack-penalty" checked={ackPenalty} onCheckedChange={(v) => setAckPenalty(!!v)} />
                <label htmlFor="ack-penalty" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Ich habe den Hinweis zur Vertragsstrafe (AGB Ziff. IV.2 g)/h) i.V.m. IV.4) gelesen.
                </label>
              </div>
            </>
          )}
          {activePrivacy && (
            <div className="flex items-center space-x-2">
              <Checkbox id="accept-privacy" checked={acceptPrivacy} onCheckedChange={(v) => setAcceptPrivacy(!!v)} />
              <label htmlFor="accept-privacy" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Ich habe die Datenschutzerklärung gelesen und akzeptiert.
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2 mt-2 sticky bottom-0 bg-background">
          <div className="mr-auto flex flex-wrap gap-2">
            {activeTerms && (
              <Button type="button" variant="outline" size="sm" onClick={() => legalAPI.downloadActive('terms')}>
                Download AGB
              </Button>
            )}
            {activePrivacy && (
              <Button type="button" variant="outline" size="sm" onClick={() => legalAPI.downloadActive('privacy')}>
                Download Datenschutzerklärung
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>Ablehnen</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isLoading}>
            {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gespeichert...</>) : 'Zustimmen und fortfahren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


